// IT LIPE multi-module chain status strip.
// Shows: Mod 1 ✓ → Mod 2 ✓ → Mod 3 ☐ + Dec-16 banner on
// sub-threshold module 3 + chain audit drawer toggle.
//
// v0.4.71d — Phase B of the IT LIPE multi-module work. The chain
// math is locked in by backend lock-in tests; this is pure
// presentation of bundle["chain"] + bundle["modules"][i].ready_to_file.
import React from "react";
import { useTranslation } from "react-i18next";

export const LipeChainStatusStrip = ({
  modules, chain, activeIndex, onSelect, onShowChainAudit,
}) => {
  const { t } = useTranslation();
  if (!modules || modules.length !== 3) return null;
  const readyAt = (i) => Boolean(modules[i]?.ready_to_file);
  const dot = (ready) =>
    ready ? (
      <span className="text-teal-700 dark:text-teal-300">✓</span>
    ) : (
      <span className="text-rose-700 dark:text-rose-300">☐</span>
    );

  return (
    <div data-testid="lipe-chain-strip" className="mb-4">
      <div className="flex items-center gap-3 flex-wrap">
        {[0, 1, 2].map((i) => (
          <React.Fragment key={i}>
            <button
              type="button"
              data-testid={`lipe-module-tab-${i + 1}`}
              onClick={() => onSelect(i)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                activeIndex === i
                  ? "bg-amber-400 text-white border-amber-400 font-bold"
                  : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-amber-300 dark:border-amber-700"
              }`}
            >
              {modules[i].module_label || t("ui.country_return.lipe.module_n", { n: i + 1, defaultValue: `Module ${i + 1}` })}{" "}
              {dot(readyAt(i))}
            </button>
            {i < 2 && (
              <span
                data-testid={`lipe-chain-arrow-${i + 1}`}
                className="text-slate-600 dark:text-slate-300"
              >
                →
              </span>
            )}
          </React.Fragment>
        ))}
        <button
          type="button"
          data-testid="lipe-chain-audit-button"
          onClick={onShowChainAudit}
          className="ml-auto text-[11px] text-teal-700 dark:text-teal-300 hover:text-teal-700 dark:hover:text-teal-300 underline"
        >
          {t("ui.country_return.lipe.audit_history", { defaultValue: "chain audit history" })}
        </button>
      </div>
      {chain && (
        <div className="mt-2 text-[11px] tabular-nums text-slate-600 dark:text-slate-300 leading-snug">
          <span data-testid="lipe-chain-m1m2">
            {t("ui.country_return.lipe.m1m2", { defaultValue: "M1→M2 credit:" })} <span className="text-slate-900 dark:text-slate-50">{chain.m1_credit_to_m2 || "0"}</span>
          </span>
          <span className="mx-3 text-slate-500 dark:text-slate-400">·</span>
          <span data-testid="lipe-chain-m2m3">
            {t("ui.country_return.lipe.m2m3", { defaultValue: "M2→M3 credit:" })} <span className="text-slate-900 dark:text-slate-50">{chain.m2_credit_to_m3 || "0"}</span>
          </span>
          <span className="mx-3 text-slate-500 dark:text-slate-400">·</span>
          <span data-testid="lipe-chain-m3next">
            {t("ui.country_return.lipe.m3next", { defaultValue: "M3→Q+1 credit:" })} <span className="text-slate-900 dark:text-slate-50">{chain.m3_credit_to_next_quarter || "0"}</span>
          </span>
        </div>
      )}
      {chain?.pay_by_dec_16 && (
        <div
          data-testid="lipe-pay-by-dec-16-banner"
          className="mt-3 p-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 text-xs leading-snug"
        >
          <div className="font-semibold mb-1">
            ⚠ {t("ui.country_return.lipe.dec16_title", { defaultValue: "VAT due of EUR 100 or less moves on" })}
          </div>
          {t("ui.country_return.lipe.dec16_before", { amount: chain.module_3_deferred_eur, defaultValue: `Month 3's VAT due of ${chain.module_3_deferred_eur} € is €100 or less, so it is paid with the next quarter's first month, where it opens` })}{" "}
          <span className="font-bold">{t("ui.country_return.lipe.dec16_date", { defaultValue: "VP7" })}</span> {t("ui.country_return.lipe.dec16_after", { defaultValue: "(debito periodo precedente). Enter 0 there if you pay it now." })}
        </div>
      )}
      {(chain?.warnings || []).map((w, i) => (
        <div
          key={i}
          data-testid={`lipe-chain-warning-${i}`}
          className="mt-2 p-2 rounded-lg border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/40 text-rose-900 dark:text-rose-100 text-[11px] leading-snug"
        >
          <span className="font-bold tabular-nums">{w.code}:</span> {w.message}
        </div>
      ))}
    </div>
  );
};
